// src/components/Lms/Pages/CourseCurriculumReview.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import "../Styles/LmsExtra.css";
import {
    FiArrowLeft,
    FiChevronDown,
    FiChevronUp,
    FiFileText,
    FiClipboard,
    FiCheckCircle, // Usado como icono genérico de "visto"
    FiMessageSquare,
} from "react-icons/fi";
import ObservacionesModal from "../Modals/ObservacionesModal";

import { ENDPOINTS as LMS_ENDPOINTS } from "../Utils/Endpoints";
import { Models } from "../Utils/ModelsLMS";
import { cursosNormalize } from "../Objetos/CursosModel";
import { moduloNormalize } from "../Objetos/ModuloModel";
import { leccionesNormalize } from "../Objetos/LeccionesModel";

const CourseCurriculumReview = () => {
    const { idCurso } = useParams(); // Usamos idCurso 
    const navigate = useNavigate();

    // ---------- State principal ----------
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedModules, setExpandedModules] = useState({});
    const [isObservacionesModalOpen, setIsObservacionesModalOpen] = useState(false);

    // ---------- Función para cargar curso completo desde BD ----------
    const loadCursoCompleto = async () => {
        if (!idCurso) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            // 1. Cargar curso y sus componentes EN PARALELO y FILTRADOS por backend (Optimizado)
            const [respCurso, respModulos, respLecciones, respCuestionarios, respExamenes, respModuloContenido] = await Promise.all([
                axios.get(LMS_ENDPOINTS.cursosById(idCurso)),
                axios.get(LMS_ENDPOINTS.moduloByCurso(idCurso)),
                axios.get(LMS_ENDPOINTS.leccionesByCurso(idCurso)),
                axios.get(LMS_ENDPOINTS.cuestionarioByCurso(idCurso)),
                axios.get(LMS_ENDPOINTS.examenByCurso(idCurso)),
                axios.get(LMS_ENDPOINTS.moduloContenidoByCurso(idCurso))
            ]);

            // Procesar Curso
            const cursoDataRaw = respCurso.data;
            const cursoData = Array.isArray(cursoDataRaw) ? cursoDataRaw[0] : cursoDataRaw;

            if (!cursoData) {
                console.warn("Curso no encontrado (ID inválido o inexistente)");
                setLoading(false);
                return;
            }
            const cursoEncontrado = cursosNormalize(cursoData);

            // Procesar Módulos
            const modulosDelCurso = (respModulos.data || [])
                .map(moduloNormalize)
                .sort((a, b) => (a.orden || 0) - (b.orden || 0));

            // Procesar Contenidos (Listas ya filtradas por el backend)
            const todasLecciones = (respLecciones.data || []).map(leccionesNormalize);
            const todosCuestionarios = respCuestionarios.data || [];
            const todosExamenes = respExamenes.data || [];
            const todoModuloContenido = (respModuloContenido.data || []).map(Models.moduloContenido);

            // 4. Mapear módulos con su contenido ORDENADO según ModuloContenido
            const modulosMapeados = modulosDelCurso.map((mod) => {
                const contenidosDelModulo = todoModuloContenido
                    .filter((mc) => mc.idModulo === mod.idModulo && mc.activo)
                    .sort((a, b) => (a.orden || 0) - (b.orden || 0));

                const contenidoMapeado = contenidosDelModulo.map((mc) => {
                    const tipo = mc.tablaContenido;

                    if (tipo === "Lecciones") {
                        const leccion = todasLecciones.find(l => l.idLeccion === mc.idAsociado);
                        if (!leccion) return null;
                        return {
                            tipo: "LECCION",
                            id: leccion.idLeccion,
                            titulo: leccion.titulo,
                            activo: leccion.activo,
                            idModuloContenido: mc.idModuloContenido,
                            idModulo: mc.idModulo
                        };
                    }

                    if (tipo === "Cuestionario") {
                        const cuestionario = todosCuestionarios.find(
                            q => (q.idCuestionario || q.IdCuestionario) === mc.idAsociado
                        );
                        if (!cuestionario) return null;

                        return {
                            tipo: "CUESTIONARIO",
                            id: cuestionario.idCuestionario || cuestionario.IdCuestionario,
                            titulo: cuestionario.nombre || cuestionario.Nombre,
                            activo: cuestionario.activo !== undefined ? cuestionario.activo : cuestionario.Activo,
                            idModuloContenido: mc.idModuloContenido,
                            idModulo: mc.idModulo
                        };
                    }

                    if (tipo === "Examen") {
                        const examen = todosExamenes.find(
                            e => (e.idExamen || e.IdExamen) === mc.idAsociado
                        );
                        if (!examen) return null;

                        return {
                            tipo: "EXAMEN",
                            id: examen.idExamen || examen.IdExamen,
                            titulo: examen.nombre || examen.Nombre,
                            activo: examen.activo !== undefined ? examen.activo : examen.Activo,
                            idModuloContenido: mc.idModuloContenido,
                            idModulo: mc.idModulo
                        };
                    }
                    return null;
                }).filter(Boolean);

                return {
                    id: mod.idModulo,
                    titulo: mod.nombre,
                    activo: mod.activo,
                    contenido: contenidoMapeado
                };
            });

            setCourse({
                ...cursoEncontrado,
                titulo: cursoEncontrado.nombre,
                modulos: modulosMapeados,
            });
            setError(null);
        } catch (err) {
            console.error("Error al cargar curso en modo revisión:", err);
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadCursoCompleto();
    }, [idCurso]);

    // Expandir módulos al cargar
    useEffect(() => {
        if (!course) return;
        setExpandedModules((prev) => {
            const next = { ...prev };
            (course.modulos || []).forEach((m) => {
                if (!(m.id in next)) {
                    next[m.id] = true;
                }
            });
            return next;
        });
    }, [course]);

    const toggleModule = (moduleId) => {
        setExpandedModules((prev) => ({
            ...prev,
            [moduleId]: !prev[moduleId],
        }));
    };

    const handleBack = () => {
        navigate('/menu/lms/catalogo-cursos');
    };

    const startReview = (moduloId, leccionId, tipo = 'leccion') => {
        // Redirigir al Player de Revisión
        // Ruta esperada: /menu/lms/curso-player-revision/:idCurso/:idModulo/:idContenido/:tipo
        navigate(`/menu/lms/curso-player-revision/${idCurso}/${moduloId}/${leccionId}/${tipo}`);
    };

    // Render principal
    if (loading) {
        return <div className="p-8 text-center text-gray-500">Cargando modo revisión...</div>;
    }

    if (error || !course) {
        return (
            <div className="p-8 text-center text-red-500">
                Error al cargar el curso.
                <br />
                <button onClick={handleBack} className="mt-4 text-blue-600 underline">
                    Volver
                </button>
            </div>
        );
    }

    return (
        <div className="cc-page">
            {/* Breadcrumb */}
            <div className="cc-breadcrumb">
                <button
                    type="button"
                    className="cc-breadcrumb-back"
                    onClick={handleBack}
                >
                    <FiArrowLeft />
                    Volver a cursos
                </button>
            </div>

            <div className="cc-layout">
                {/* MAIN */}
                <div className="cc-main">
                    {/* Título de curso (sin edición en modo revisión) */}
                    <h1 className="cc-course-title">
                        {course.titulo}
                    </h1>

                    {/* Chips resumen */}
                    <div className="cc-chip-row">
                        <span className="cc-chip">
                            {course.modulos?.length || 0} módulos
                        </span>
                        <span className="cc-chip">
                            {course.modulos?.reduce((acc, m) =>
                                acc + (m.contenido?.filter(c => c.tipo === 'LECCION').length || 0), 0
                            )} lecciones
                        </span>
                        <span className="cc-chip">
                            {course.modulos?.reduce((acc, m) =>
                                acc + (m.contenido?.filter(c => c.tipo === 'CUESTIONARIO').length || 0), 0
                            )} cuestionarios
                        </span>
                        <span className="cc-chip">
                            {course.modulos?.reduce((acc, m) =>
                                acc + (m.contenido?.filter(c => c.tipo === 'EXAMEN').length || 0), 0
                            )} evaluaciones
                        </span>
                    </div>

                    {/* Toolbar */}
                    <div className="cc-toolbar">
                        <button
                            type="button"
                            className="cc-add-lesson-btn"
                            onClick={() => {
                                const allExpanded = Object.values(expandedModules).every(v => v);
                                const newState = {};
                                (course.modulos || []).forEach(m => {
                                    newState[m.id] = !allExpanded;
                                });
                                setExpandedModules(newState);
                            }}
                        >
                            {Object.values(expandedModules).every(v => v)
                                ? "Contraer todas las secciones"
                                : "Expandir todas las secciones"}
                        </button>
                    </div>

                    {/* Lista de módulos */}
                    <div className="cc-modules-list">
                        {course.modulos && course.modulos.length > 0 ? (
                            course.modulos.map((modulo, index) => {
                                const isExpanded = expandedModules[modulo.id];
                                const contentCount = (modulo.contenido || []).length;

                                return (
                                    <div
                                        key={modulo.id}
                                        className="cc-module-card"
                                    >
                                        {/* Header módulo */}
                                        <div className="cc-module-header">
                                            <div className="cc-module-header-left">
                                                <div className="cc-module-index-badge">
                                                    {index + 1}
                                                </div>
                                                <div>
                                                    <h2 className="cc-module-title">
                                                        {modulo.titulo}
                                                    </h2>
                                                    <p className="cc-module-meta">
                                                        {contentCount}{" "}
                                                        {contentCount === 1
                                                            ? "elemento"
                                                            : "elementos"}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="cc-module-header-right">
                                                <button
                                                    type="button"
                                                    className="cc-module-toggle-btn"
                                                    onClick={() => toggleModule(modulo.id)}
                                                    title={isExpanded ? "Contraer" : "Expandir"}
                                                >
                                                    {isExpanded ? (
                                                        <FiChevronUp />
                                                    ) : (
                                                        <FiChevronDown />
                                                    )}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Contenido del módulo (si expandido) */}
                                        {isExpanded && (
                                            <div className="cc-lessons-list">
                                                {(modulo.contenido || []).length === 0 && (
                                                    <p className="cc-empty-lessons">
                                                        No hay contenido. Presiona "+ Nuevo" para agregar.
                                                    </p>
                                                )}

                                                {(modulo.contenido || []).map((item, itemIndex) => {
                                                    // Determinar icono según tipo
                                                    const Icon = item.tipo === "LECCION"
                                                        ? FiFileText
                                                        : item.tipo === "CUESTIONARIO"
                                                            ? FiClipboard
                                                            : FiCheckCircle;

                                                    return (
                                                        <div
                                                            key={`${item.tipo}-${item.id}`}
                                                            className="cc-lesson-row"
                                                        >
                                                            {/* Main */}
                                                            <div
                                                                className="cc-lesson-main"
                                                                onClick={() => startReview(modulo.id, item.id, item.tipo)}
                                                            >
                                                                <div className="cc-lesson-icon">
                                                                    <Icon />
                                                                </div>
                                                                <div className="cc-lesson-texts">
                                                                    <h3 className="cc-lesson-title">
                                                                        {item.tipo === "CUESTIONARIO" && "📝 "}
                                                                        {item.tipo === "EXAMEN" && "📋 "}
                                                                        {item.titulo}
                                                                    </h3>
                                                                </div>
                                                            </div>

                                                            {/* Actions */}
                                                            <div className="cc-lesson-actions">
                                                                <button
                                                                    type="button"
                                                                    className="cc-lesson-btn cc-lesson-btn-ghost"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        startReview(modulo.id, item.id, item.tipo);
                                                                    }}
                                                                    title="Ver contenido"
                                                                >
                                                                    <FiFileText /> Ver
                                                                </button>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        ) : (
                            <div className="text-center p-12 bg-white rounded-xl border border-gray-200">
                                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                                    Curso vacío
                                </h3>
                                <p className="text-gray-500">
                                    No hay módulos configurados para este curso.
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* SIDEBAR (Read-only summary) */}
                <aside className="cc-sidebar">
                    <div className="cc-summary-card">
                        <div className="cc-summary-status-pill" style={{ background: '#eab308', color: '#fff' }}>
                            Modo Revisión
                        </div>
                        <h3 className="cc-summary-title cc-summary-title-student">
                            Resumen del curso
                        </h3>

                        <div className="cc-summary-grid">
                            <div className="cc-summary-item">
                                <small className="cc-summary-label">
                                    Duración
                                </small>
                                <strong className="cc-summary-value">
                                    {course.duracion || 0} días
                                </strong>
                            </div>
                            <div className="cc-summary-item">
                                <small className="cc-summary-label">
                                    Evaluaciones
                                </small>
                                <strong className="cc-summary-value">
                                    {course.modulos?.reduce((acc, m) =>
                                        acc + (m.contenido?.filter(c => c.tipo === 'EXAMEN').length || 0), 0
                                    )}
                                </strong>
                            </div>
                            <div className="cc-summary-item">
                                <small className="cc-summary-label">
                                    Módulos
                                </small>
                                <strong className="cc-summary-value">
                                    {course.modulos?.length || 0}
                                </strong>
                            </div>
                            <div className="cc-summary-item">
                                <small className="cc-summary-label">
                                    Lecciones
                                </small>
                                <strong className="cc-summary-value">
                                    {course.modulos?.reduce((acc, m) =>
                                        acc + (m.contenido?.filter(c => c.tipo === 'LECCION').length || 0), 0
                                    )}
                                </strong>
                            </div>
                            <div className="cc-summary-item">
                                <small className="cc-summary-label">
                                    Cuestionarios
                                </small>
                                <strong className="cc-summary-value">
                                    {course.modulos?.reduce((acc, m) =>
                                        acc + (m.contenido?.filter(c => c.tipo === 'CUESTIONARIO').length || 0), 0
                                    )}
                                </strong>
                            </div>
                        </div>

                        {/* Separador visual */}
                        <hr style={{ margin: '1.5rem 0', border: '0', borderTop: '1px solid #e2e8f0' }} />

                        <button
                            type="button"
                            className="cc-summary-btn-main"
                            onClick={() => navigate('/menu/dynamic?path=Lms/Components/Catalogos/Certificaciones/CertificacionesCatalog.jsx')}
                            style={{
                                marginBottom: '1rem',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            }}
                        >
                            📜 Gestionar Certificado
                        </button>

                        <p className="text-xs text-gray-400 text-center">
                            Estás navegando como administrador. <br /> El progreso no se registrará.
                        </p>
                    </div>
                </aside>
            </div>

            {/* Modal de Observaciones */}
            <ObservacionesModal
                isOpen={isObservacionesModalOpen}
                onClose={() => setIsObservacionesModalOpen(false)}
                idCurso={idCurso}
            />

            {/* Floating Button for Observations */}
            <button
                className="floating-observaciones-btn"
                onClick={() => setIsObservacionesModalOpen(true)}
                title="Historial de Observaciones"
                style={{
                    position: 'fixed',
                    bottom: '30px',
                    right: '30px',
                    backgroundColor: 'var(--color-primary)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '50%',
                    width: '60px',
                    height: '60px',
                    fontSize: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
                    cursor: 'pointer',
                    zIndex: 1000
                }}
            >
                <FiMessageSquare />
            </button>
        </div>
    );
};

export default CourseCurriculumReview;
